import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getWorkspaces } from "@/api/knowledgeGraphApi";

interface UseKgGraphNavigationOptions {
	session: boolean;
	useMock: boolean;
}

export function useKgGraphNavigation({ session, useMock }: UseKgGraphNavigationOptions) {
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedGraphId, setSelectedGraphId] = useState<string | null>(null);
	const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
	const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
	const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
	const [graphIdInput, setGraphIdInput] = useState("");
	const hasAutoSelectedRef = useRef(false);

	const graphIdFromUrl = searchParams.get("graphId");
	const workspaceIdFromUrl = searchParams.get("workspaceId");

	const { data: workspacesData } = useQuery({
		queryKey: ["workspaces"],
		queryFn: ({ signal }) => getWorkspaces({ signal }),
		enabled: session && !useMock,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (!workspacesData?.length) return;
		const fromUrl = workspaceIdFromUrl && workspacesData.find((w) => w.id === workspaceIdFromUrl);
		setSelectedWorkspaceId(fromUrl ? fromUrl.id : (workspacesData[0]?.id ?? null));
	}, [workspacesData, workspaceIdFromUrl]);

	useEffect(() => {
		if (!graphIdFromUrl || hasAutoSelectedRef.current) return;
		setSelectedGraphId(graphIdFromUrl);
		setGraphIdInput(graphIdFromUrl);
		hasAutoSelectedRef.current = true;
	}, [graphIdFromUrl]);

	const clearSelection = useCallback(() => {
		setSelectedGraphId(null);
		setSelectedNodeId(null);
		setSelectedEdgeId(null);
	}, []);

	const handleWorkspaceChange = useCallback(
		(workspaceId: string) => {
			setSelectedWorkspaceId(workspaceId);
			clearSelection();
			const next = new URLSearchParams(searchParams);
			next.set("workspaceId", workspaceId);
			next.delete("graphId");
			setSearchParams(next, { replace: true });
		},
		[searchParams, setSearchParams, clearSelection],
	);

	const openGraphById = useCallback(
		(id: string) => {
			const trimmed = id.trim();
			if (!trimmed) return;
			setSelectedGraphId(trimmed);
			setSelectedNodeId(null);
			setSelectedEdgeId(null);
			const next = new URLSearchParams(searchParams);
			next.set("graphId", trimmed);
			setSearchParams(next, { replace: true });
			hasAutoSelectedRef.current = true;
		},
		[searchParams, setSearchParams],
	);

	const handleBackToWorkspace = useCallback(() => {
		clearSelection();
		const next = new URLSearchParams(searchParams);
		next.delete("graphId");
		setSearchParams(next, { replace: true });
	}, [searchParams, setSearchParams, clearSelection]);

	return {
		selectedGraphId,
		selectedNodeId,
		setSelectedNodeId,
		selectedEdgeId,
		setSelectedEdgeId,
		selectedWorkspaceId,
		graphIdInput,
		setGraphIdInput,
		workspacesData,
		openGraphById,
		handleBackToWorkspace,
		handleWorkspaceChange,
		clearSelection,
	};
}
